import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Cpu, Brain, Users, Rocket, ArrowRight, CheckCircle } from 'lucide-react';
import SEO from '../components/SEO';
import { servicePillars, images } from '../data/mock';

const iconMap = {
  Cpu: Cpu,
  Brain: Brain,
  Users: Users,
  Rocket: Rocket,
};

const ServicesPage = () => {
  return (
    <div className="min-h-screen pt-20">
      <SEO 
        title="Our Services"
        description="TekClap delivers strategic value across four practice areas: Technology Consulting, AI Consulting, Product Development, and Workforce & Talent Solutions."
        keywords="technology consulting, AI consulting, product development, workforce solutions, digital transformation"
      />
      
      {/* Hero Section */}
      <section className="py-20 relative overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src={images.heroAlt}
            alt="Technology Innovation"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-white/95 via-white/90 to-white/80" />
        </div>
        <div className="container-custom relative z-10">
          <div className="max-w-3xl">
            <span className="text-[#F58B23] font-medium text-sm uppercase tracking-wider mb-4 block">
              Our Services
            </span>
            <h1 className="text-4xl md:text-5xl font-serif font-bold text-[#1A1A1A] mb-6">
              Comprehensive Solutions for Modern Enterprises
            </h1>
            <p className="text-lg text-[#6B7280] leading-relaxed">
              TekClap delivers strategic value across four interconnected practice areas: 
              Technology Consulting, AI Consulting, Product Development, and Workforce & 
              Talent Solutions. Each practice area is designed to work independently or 
              together to address your most pressing business challenges.
            </p>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 bg-white">
        <div className="container-custom">
          <div className="space-y-20">
            {servicePillars.map((pillar, index) => {
              const IconComponent = iconMap[pillar.icon] || Cpu;
              const isEven = index % 2 === 0;
              
              return (
                <div
                  key={pillar.id}
                  className={`grid lg:grid-cols-2 gap-12 items-center`}
                >
                  <div className={!isEven ? 'lg:order-2' : ''}>
                    <div className="w-16 h-16 bg-[#FFF4E6] rounded-2xl flex items-center justify-center mb-6">
                      <IconComponent className="w-8 h-8 text-[#F58B23]" />
                    </div>
                    <h2 className="text-3xl font-serif font-bold text-[#1A1A1A] mb-4">
                      {pillar.title}
                    </h2>
                    <p className="text-[#6B7280] mb-6 leading-relaxed text-lg">
                      {pillar.description}
                    </p>
                    <Link to={pillar.link}>
                      <Button className="bg-[#F58B23] hover:bg-[#D97706] text-white">
                        Explore {pillar.title.split(' ')[0]}
                        <ArrowRight className="ml-2 w-4 h-4" />
                      </Button>
                    </Link>
                  </div>
                  
                  <div className={`relative rounded-2xl overflow-hidden ${!isEven ? 'lg:order-1' : ''}`}>
                    <img
                      src={pillar.image}
                      alt={pillar.title}
                      className="w-full h-80 object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Quick Links Section */}
      <section className="py-16 bg-[#FAFAF8]">
        <div className="container-custom">
          <h2 className="text-2xl font-serif font-bold text-[#1A1A1A] mb-8 text-center">
            Explore Our Services
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <Link to="/technology-consulting" className="group bg-white p-6 rounded-xl shadow-sm hover:shadow-md transition-all">
              <Cpu className="w-8 h-8 text-[#F58B23] mb-4" />
              <h3 className="font-semibold text-[#1A1A1A] mb-2 group-hover:text-[#F58B23] transition-colors">
                Technology Consulting
              </h3>
              <p className="text-sm text-[#6B7280]">Digital transformation & strategy</p>
            </Link>
            <Link to="/ai-consulting" className="group bg-white p-6 rounded-xl shadow-sm hover:shadow-md transition-all">
              <Brain className="w-8 h-8 text-[#F58B23] mb-4" />
              <h3 className="font-semibold text-[#1A1A1A] mb-2 group-hover:text-[#F58B23] transition-colors">
                AI Consulting
              </h3>
              <p className="text-sm text-[#6B7280]">AI strategy & intelligent automation</p>
            </Link>
            <Link to="/product-development" className="group bg-white p-6 rounded-xl shadow-sm hover:shadow-md transition-all">
              <Rocket className="w-8 h-8 text-[#F58B23] mb-4" />
              <h3 className="font-semibold text-[#1A1A1A] mb-2 group-hover:text-[#F58B23] transition-colors">
                Product Development
              </h3>
              <p className="text-sm text-[#6B7280]">Cloud-native & AI-powered products</p>
            </Link>
            <Link to="/workforce-solutions" className="group bg-white p-6 rounded-xl shadow-sm hover:shadow-md transition-all">
              <Users className="w-8 h-8 text-[#F58B23] mb-4" />
              <h3 className="font-semibold text-[#1A1A1A] mb-2 group-hover:text-[#F58B23] transition-colors">
                Workforce Solutions
              </h3>
              <p className="text-sm text-[#6B7280]">RPO, staffing & talent solutions</p>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-[#1A1A1A]">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-serif font-bold text-white mb-6">
              Not Sure Where to Start?
            </h2>
            <p className="text-gray-400 mb-8 text-lg">
              Our consultants can help you identify the right combination of services 
              to address your specific challenges.
            </p>
            <Link to="/contact">
              <Button
                size="lg"
                className="bg-[#F58B23] hover:bg-[#D97706] text-white font-medium px-10"
              >
                Schedule a Discovery Call
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ServicesPage;
